import { useEffect, useState } from 'react';

import { Archive03Icon, Delete02Icon } from '@hugeicons/core-free-icons';
import { useTranslation } from 'react-i18next';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Button, InputGroup, Select, Toggle } from 'components/FormComponents';

import styles from './FormReplacementModal.module.scss';

export const FormReplacementModal = ({
  visible,
  action,
  bundles = [],
  replacementOptions = [],
  isSubmitting = false,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [useSameForm, setUseSameForm] = useState(true);
  const [sameFormId, setSameFormId] = useState('');
  const [bundleReplacements, setBundleReplacements] = useState({});

  useEffect(() => {
    if (!visible) {
      setUseSameForm(true);
      setSameFormId('');
      setBundleReplacements({});
    }
  }, [visible]);

  const hasReplacementOptions = replacementOptions.length > 0;
  const replacements = useSameForm
    ? Object.fromEntries(bundles.map(bundle => [bundle.id, sameFormId]))
    : bundleReplacements;
  const allBundlesHaveReplacement = bundles.every(bundle => replacements[bundle.id]);

  const handleToggleSameForm = event => {
    const checked = event.target.checked;
    if (!checked) {
      setBundleReplacements(Object.fromEntries(bundles.map(bundle => [bundle.id, sameFormId])));
    }
    setUseSameForm(checked);
  };

  const handleBundleReplacement = (bundleId, formId) => {
    setBundleReplacements(current => ({ ...current, [bundleId]: formId }));
  };

  const renderOptions = () =>
    replacementOptions.map(form => (
      <Select.Item key={form.id} value={form.id}>
        {form.name}
      </Select.Item>
    ));

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      noClose={isSubmitting}
      title={t(`questionnaires.formReplacement.${action}.title`)}
      description={t(`questionnaires.formReplacement.${action}.description`)}
      icon={action === 'delete' ? Delete02Icon : Archive03Icon}
      oldIconLibrary={false}
      small>
      <div className="flex flex-col gap-2">
        {hasReplacementOptions ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xl text-neutral-6">
                {t('questionnaires.formReplacement.sameFormToggleLabel')}
              </span>
              <Toggle
                name="use_same_form"
                inputProps={{ checked: useSameForm, onChange: handleToggleSameForm }}
              />
            </div>

            {useSameForm ? (
              <>
                <InputGroup title={t('questionnaires.formReplacement.bundlesLabel')}>
                  <ul className={styles.bundleList}>
                    {bundles.map(bundle => (
                      <li key={bundle.id}>{bundle.name}</li>
                    ))}
                  </ul>
                </InputGroup>
                <InputGroup title={t('questionnaires.formReplacement.replacementLabel')}>
                  <Select
                    name="same_form"
                    placeholder={t('questionnaires.formReplacement.replacementPlaceholder')}
                    value={sameFormId}
                    onChange={event => setSameFormId(event.target.value)}>
                    {renderOptions()}
                  </Select>
                </InputGroup>
              </>
            ) : (
              bundles.map(bundle => (
                <InputGroup key={bundle.id} title={bundle.name}>
                  <Select
                    name={`replacement_${bundle.id}`}
                    placeholder={t('questionnaires.formReplacement.replacementPlaceholder')}
                    value={bundleReplacements[bundle.id] || ''}
                    onChange={event => handleBundleReplacement(bundle.id, event.target.value)}>
                    {renderOptions()}
                  </Select>
                </InputGroup>
              ))
            )}
          </>
        ) : (
          <span className="text-xl text-neutral-6">
            {t('questionnaires.formReplacement.noReplacement')}
          </span>
        )}

        <div className="flex justify-end gap-2">
          <Button type="secondary" onClick={onClose} disabled={isSubmitting}>
            {t('questionnaires.formReplacement.cancel')}
          </Button>
          <Button
            loading={isSubmitting}
            disabled={!hasReplacementOptions || !allBundlesHaveReplacement || isSubmitting}
            onClick={() => onConfirm(replacements)}>
            {t(`questionnaires.formReplacement.${action}.confirm`)}
          </Button>
        </div>
      </div>
    </AnimatedModal>
  );
};
